import React, { useMemo, useRef, useState } from 'react';
import { searchGuide, openHit, Hit, TOP_HITS, GlassSearchIcon } from './GuideSheet';
import { IllustrationDefs, HeroArt } from './Illustrations';

interface HeroProps {
  onOpenSplash?: () => void;
}

// "Ask the guide" hero: a drawn poster of the skyline and the house, one left-aligned column
// on the right (the Space Needle stays clear on the left): headline, guide search
// (results open right under the bar), lede, rating and route button; facts strip along the bottom.
// Phones hand the search off to the guide's own search card above the keyboard.

const RATING = '4.99';
const REVIEW_COUNT = '100+';

// Facts strip, in the order it reads; italic entries are the little asides.
export const HERO_FACTS: { text: string; aside?: boolean }[] = [
  { text: 'Walk Score 96' },
  { text: '431 Mbps Wi-Fi' },
  { text: 'Sleeps six' },
  { text: 'made for slow mornings', aside: true },
  { text: 'Rooftop grill' },
  { text: 'Built Green ★★★★' },
  { text: 'Own parking spot' },
];

// Search is keyword-only, so the chips are words it knows.
const MONTH = new Date().toLocaleString('en-US', { month: 'long' });
const CHIPS = [
  { icon: '☕', label: 'Coffee', q: 'coffee' },
  { icon: '📶', label: 'Wi-Fi', q: 'wifi' },
  { icon: '🚆', label: 'Light rail', q: 'light rail' },
  { icon: '🗓', label: `What's on in ${MONTH}`, q: MONTH },
  { icon: '☔', label: 'Rainy day', q: 'rainy' },
  { icon: '🥞', label: 'Brunch', q: 'brunch' },
].map((c) => ({ ...c, count: searchGuide(c.q)?.length ?? 0 }));

const hitIcon = (h: Hit) =>
  h.href ? '🏠' : h.spot ? '🚶' : h.area ? '🗺' : h.event ? '🗓' : h.place?.kind === 'restaurant' ? '🍽' : '📍';

const isPhone = () => !!window.matchMedia?.('(max-width: 768px)').matches;
const openSheet = (query = '', all = false) =>
  window.dispatchEvent(new CustomEvent('corazon:open-search', { detail: { query, all } }));

const FactsRun: React.FC<{ hidden?: boolean }> = ({ hidden }) => (
  <ul className="ed-ticker-run" aria-hidden={hidden || undefined}>
    {HERO_FACTS.map((f) => (
      <li key={f.text} className={f.aside ? 'is-aside' : undefined}>
        {f.text}
        <span className="ed-ticker-star" aria-hidden="true">✶</span>
      </li>
    ))}
  </ul>
);

// Its own component so typing re-renders only the search, never the headline
// (useTextReveal has split that into words).
const HeroAsk: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const results = useMemo(() => searchGuide(query), [query]);
  const shown = (results ?? []).slice(0, TOP_HITS);

  const reset = () => {
    setQuery('');
    setActive(0);
    inputRef.current?.blur();
  };

  const ask = (q: string) => {
    if (isPhone()) return openSheet(q);
    setQuery(q);
    setActive(0);
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') reset();
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      if (shown.length) setActive((i) => (i + step + shown.length) % shown.length);
    }
  };

  // Enter (or the button) opens the highlighted result.
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shown.length) return ask(query);
    listRef.current?.querySelectorAll<HTMLElement>('a, button')[active]?.click();
  };

  const isOpen = open && results !== null;

  return (
    <div className={`ed-ask-wrap ${isOpen ? 'is-open' : ''}`}>
      <form
        className="ed-ask"
        role="search"
        onSubmit={onSubmit}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <label className="ed-ask-bar">
          <GlassSearchIcon />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={(e) => {
              // Phones: the guide's search card sits above the keyboard instead.
              if (!isPhone()) return;
              e.currentTarget.blur();
              openSheet(query);
            }}
            onKeyDown={onKeyDown}
            placeholder="Coffee, Wi-Fi, light rail, what's on…"
            aria-label="Ask the guide"
            enterKeyHint="search"
            autoComplete="off"
          />
          <kbd aria-hidden="true">{query ? 'esc' : '/'}</kbd>
          <button type="submit" className="ed-ask-go">
            <span>Ask the guide</span>
          </button>
        </label>

        {isOpen && (
          // mousedown would blur the field and close the list before the click lands.
          <div className="ed-ask-results" aria-live="polite" onMouseDown={(e) => e.preventDefault()}>
            {shown.length === 0 ? (
              <p className="ed-ask-empty">No matches for “{query.trim()}”. Try “coffee”, “park” or “Wi-Fi”.</p>
            ) : (
              <>
                <ul ref={listRef}>
                  {shown.map((h, i) => {
                    const body = (
                      <>
                        <i aria-hidden="true">{hitIcon(h)}</i>
                        <span>
                          <strong>{h.label}</strong>
                          <small>{h.meta}</small>
                        </span>
                      </>
                    );
                    return (
                      <li key={h.key} className={i === active ? 'is-active' : undefined} onMouseEnter={() => setActive(i)}>
                        {h.href ? (
                          <a href={h.href} onClick={reset}>{body}</a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              reset();
                              openHit(h);
                            }}
                          >
                            {body}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="ed-ask-more">
                  {(results?.length ?? 0) > TOP_HITS ? (
                    <button
                      type="button"
                      onClick={() => {
                        openSheet(query, true);
                        reset();
                      }}
                    >
                      See all {results?.length} results
                    </button>
                  ) : (
                    <span />
                  )}
                  <span aria-hidden="true">↑↓ to move · ↵ to open</span>
                </div>
              </>
            )}
          </div>
        )}
      </form>

      <ul className="ed-ask-chips" aria-label="Try asking">
        {CHIPS.map((c) => (
          <li key={c.q}>
            <button type="button" onClick={() => ask(c.q)}>
              <span aria-hidden="true">{c.icon}</span> {c.label}
              {c.count > 1 && <b>{c.count}</b>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Hero: React.FC<HeroProps> = () => (
  <section id="hero" className="hero ed-hero">
    <IllustrationDefs />
    <div
      className="ed-hero-photo"
      role="img"
      aria-label="Illustration of Seattle's skyline, the Space Needle and Mt. Rainier behind the red townhouse on Capitol Hill"
    >
      <HeroArt />
      <HeroArt phone />
    </div>

    <div className="ed-hero-stage">
      <div className="ed-hero-copy">
        <p className="hero-eyebrow ed-tag">Seattle · Capitol Hill</p>
        <h1 className="hero-title ed-hero-title">
          Hello <em>from the</em> Hill.
          <br />
          <span className="ed-fade">Ask <b className="ed-brand">Corazón</b> anything.</span>
        </h1>
        <HeroAsk />
        <div className="ed-hero-row">
          <p className="hero-subtitle">
            Everything for an easy Seattle stay.{' '}
            <span className="ed-fade">
              Before arrival, while you&apos;re here, and when it&apos;s time to leave. Four floors, a rooftop, and Capitol
              Hill at your door.
            </span>
          </p>
          <div className="ed-hero-cta">
            <span className="ed-stamp">
              <b>{RATING} <i aria-hidden="true">★</i></b>
              <small>{REVIEW_COUNT} reviews</small>
            </span>
            <a className="ed-btn" href="#stay-guide">
              Start your stay route <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div className="ed-ticker" role="region" aria-label="The house at a glance">
      <div className="ed-ticker-track">
        <FactsRun />
        <FactsRun hidden />
      </div>
    </div>
  </section>
);

export default Hero;
